import { ControlledTextField } from '@if/mui'
import { CardContent, CardHeader, Grid, Stack, Typography } from '@mui/material'
import { SelectComponent } from '@src/components'
import { CardBorderTop } from '@src/components/Card'
import { FinancingProgramProvider } from '@src/data/types/FinancingProgramProvider'
import { MechanicalWarrantyProduct } from '@src/data/types/MechanicalWarrantyProduct'
import { ChangeEvent, useEffect, useMemo } from 'react'
import { Control, UseFormSetValue, useWatch } from 'react-hook-form'; // Import useWatch here
import { useTranslation } from 'react-i18next'
import CheckboxGroupField from '../../../../../components/CheckboxGroupField'
import {
  EMechanicalWarrantyOptions,
  EMechanicalWarrantySurcharges,
  MechanicalWarrantyWorksheetDto
} from '../../types/mechanicalWarrantyWorksheetSchema'

interface Props {
  merchantMechanicalWarrantyProvider: FinancingProgramProvider
  mechanicalWarrantyProducts: MechanicalWarrantyProduct[]
  control: Control<MechanicalWarrantyWorksheetDto>
  setValue: UseFormSetValue<MechanicalWarrantyWorksheetDto>
}

const MechanicalWarrantyExtended = ({ 
  merchantMechanicalWarrantyProvider, 
  mechanicalWarrantyProducts, 
  control, 
  setValue 
}: Props) => {
  const { t, i18n } = useTranslation()

  const mechanicalWarrantyProductId = useWatch({ 
    control, 
    name: 'mechanicalWarrantyProductId' 
  }) ?? ''

  // Auto-set provider ID when component mounts or provider changes
  useEffect(() => {
    if (merchantMechanicalWarrantyProvider?.id) {
      setValue('providerId', merchantMechanicalWarrantyProvider.id)
    }
  }, [merchantMechanicalWarrantyProvider?.id, setValue])

  const providerProducts = useMemo(() => {
    const ret = mechanicalWarrantyProducts
      .map((product) => ({
        label: i18n.language === 'fr'
              ? product.nameFr
              : product.nameEn,
        value: product.id,
      }))

    return ret
  }, [mechanicalWarrantyProducts, i18n.language])

  return (
    <CardBorderTop>
      <CardHeader title={t('mechanicalWarrantyWorksheet.extendedWarranty')} />
      <CardContent component={Stack} spacing={2}>
        <Typography variant="body1" fontWeight="bold">
          {t('mechanicalWarrantyWorksheet.providerLabel')}
        </Typography>
        <Grid sx={{ mb: 3 }}>
          <Typography variant="body1">
            {i18n.language === 'fr'
              ? merchantMechanicalWarrantyProvider.nameFr
              : merchantMechanicalWarrantyProvider.nameEn}
          </Typography>
        </Grid>

        <Typography variant="body1" fontWeight="bold">
          {t('mechanicalWarrantyWorksheet.productLabel')}
        </Typography>
        <Grid sx={{ mb: 3 }}>
          <SelectComponent
            items={providerProducts}
            name="mechanicalWarrantyWorksheet.productLabel"
            value={mechanicalWarrantyProductId}
            onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
              setValue('mechanicalWarrantyProductId', e.target.value)
            }}
            label={t('mechanicalWarrantyWorksheet.productLabel')}
          />
        </Grid>
        <Stack>
          <Typography variant="subtitle2">{t('mechanicalWarrantyWorksheet.surcharge')}</Typography>
          <CheckboxGroupField
            name="additions.surcharges"
            control={control}
            options={[
              { label: t('mechanicalWarrantyWorksheet.electric'), value: EMechanicalWarrantySurcharges.Electric },
              { label: t('mechanicalWarrantyWorksheet.luxury'), value: EMechanicalWarrantySurcharges.Luxury },
            ]}
          />
          <Typography variant="subtitle2">{t('mechanicalWarrantyWorksheet.option')}</Typography>
          <CheckboxGroupField
            name="additions.options"
            control={control}
            options={[
              {
                label: t('mechanicalWarrantyWorksheet.highTechnology'),
                value: EMechanicalWarrantyOptions.HighTechnology,
              },
            ]}
          />
        </Stack>
        <ControlledTextField
          label={t('mechanicalWarrantyWorksheet.policy')}
          control={control}
          name="policy"
          required
        />
      </CardContent>
    </CardBorderTop>
  )
}

export default MechanicalWarrantyExtended